<template>
  <q-banner
      v-bind="bindContainer"
      class="alert"
  >
    <template v-slot:avatar v-if="!isMultiple && iconSize">
      <q-icon
          v-bind="bindIcon"
          :size="iconSize"
          class="q-py-xs"
      />
    </template>
    <template v-if="isMultiple">

      <div class="message-rows q-gutter-y-xs">
        <div
            v-for="(msg, index) of message.messages"
            :key="index"
            class="flex q-gutter-x-sm no-wrap items-center"
        >
          <div v-if="iconSize">
            <q-icon
                v-bind="bindIcon"
                :size="iconSize"
            />
          </div>
          <div
              class="leading-e5"
              :class="messageClass"
          >
            {{ msg }}
          </div>
        </div>
      </div>

      <div v-if="actions.length" class="q-mt-xs">
        <div class="flex q-gutter-x-sm no-wrap items-center">
          <div v-if="iconSize">
            <q-icon
                v-bind="bindIcon"
                :size="iconSize"
                :style="{visibility: 'hidden'}"
            />
          </div>
          <div class="leading-e5">
            <div class="flex q-gutter-sm">
              <q-btn
                  v-bind="action"
                  size="md"
                  v-for="(action, index) of actions"
                  :key="index"
              />
            </div>
          </div>
        </div>
      </div>

    </template>
    <template v-else>
      <div :class="messageClass">
        {{ message.message }}
      </div>
      <div class="flex q-gutter-sm q-pt-sm" v-if="actions.length && !inlineActions">
        <q-btn
            v-bind="action"
            size="md"
            v-for="(action, index) of actions"
            :key="index"
        />
      </div>
    </template>
    <template v-slot:action v-if="inlineActions && actions.length">
      <q-btn
          v-bind="action"
          size="md"
          v-for="(action, index) of actions"
          :key="index"
      />
    </template>
  </q-banner>
</template>

<script lang="ts" setup>

import { ICONS } from '@/assets/icons';
import { menuItemToButton } from "@/core/util/project/menuItemToButton";
import { ComponentProps } from "@/core/vue/types";
import { Message, MessageTypeEnum } from "@/gql/gen";
import { QBannerProps, QBtnProps, QIcon, QIconProps } from "quasar";
import { computed, toRefs } from "vue";

const schema: Record<MessageTypeEnum, {
  color: string,
  class: string,
  classOutline: string,
  icon: any,
  iconColor: string,
  iconColorOutline: string,
}> = {
  info: {
    color: 'green',
    class: 'text-white bg-green',
    classOutline: 'text-green',
    icon: ICONS.infoCircle,
    iconColor: 'white',
    iconColorOutline: 'green',
  },
  error: {
    color: 'red',
    class: 'text-white bg-red',
    classOutline: 'text-red',
    icon: ICONS.exclamationTriangle,
    iconColor: 'white',
    iconColorOutline: 'red',
  },
  success: {
    color: 'green',
    class: 'text-white bg-green',
    classOutline: 'text-green',
    icon: ICONS.checkCircleAlt,
    iconColor: 'white',
    iconColorOutline: 'green',
  },
  warning: {
    color: 'yellow-10',
    class: 'text-white bg-yellow-10',
    classOutline: 'text-yellow-10',
    icon: ICONS.infoCircle,
    iconColor: 'white',
    iconColorOutline: 'yellow-10',
  }
}

export type AlertPropsBase = QBannerProps & {
  messageClass?: any,
  message: Partial<Message>,
  outlined?: boolean,
  flat?: boolean,
  duration?: number | null,
  actionProps?: QBtnProps,
  iconSize?: string | false,
}

export type AlertProps = AlertPropsBase & {}

const props = withDefaults(defineProps<AlertProps>(), {
  messageClass: 's-font-sm',
  iconSize: '20px',
  actionProps: () => ({})
})

//const {} = props
const {message, flat, outlined, actionProps} = toRefs(props)

const typeInfo = computed(() => {
  return schema[message.value?.type || 'info']
})

const actions = computed(() => {
  return (message.value.actions || []).map(action => {

    return {
      dense: true,
      class: 'q-px-md',
      unelevated: true,
      ...((res: QBtnProps) => {
        if (outlined.value) {
          res.color = typeInfo.value.color
        } else {
          res.color = "white"
          res.outline = true
        }
        return res
      })({}),
      ...actionProps.value,
      ...menuItemToButton(action),
    }
  })
})

const bindContainer = computed(() => {
  let res: ComponentProps<QBannerProps> = {
    class: {},
    inlineActions: props.inlineActions
  }
  if (flat.value) {
    res.class[typeInfo.value.classOutline] = true
  } else if (outlined.value) {
    res.class['--outlined'] = true
    res.class[typeInfo.value.classOutline] = true
  } else {
    res.class['--outlined'] = true
    res.class[typeInfo.value.class] = true
  }
  return res
})

const bindIcon = computed(() => {
  let res: QIconProps = {
    name: typeInfo.value.icon,
    color: outlined.value || flat.value ? typeInfo.value.iconColorOutline : undefined,
  }
  return res
})

const isMultiple = computed(() => {
  return !!message.value.messages?.length
})

</script>

<style lang="scss" scoped>
.alert {

  border-radius: 10px;
  min-height: auto;

  :deep() {
    .q-banner__avatar {
      //align-self: center;
    }

    .q-banner__content {
      font-size: 17px;
    }
  }

  &.--outlined {
    border: 1px solid currentColor;
  }

  .message-rows {
    li {
      list-style: square;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
